import {
  MathUtils,
  PerspectiveCamera,
  Vector3,
} from "three";

import type {
  CameraCollisionQuery,
  CameraRenderConfig,
  RenderVector3,
} from "./types.ts";
import { ROAD_VEHICLE_CAMERA_TUNING } from "../config/vehicles.ts";

export interface CameraRigUpdate {
  readonly playerPosition: RenderVector3;
  readonly viewYawRadians: number;
  readonly deltaSeconds: number;
  readonly isSprinting: boolean;
  readonly targetHeightOffsetMeters?: number;
  readonly distanceMeters?: number;
  readonly shoulderOffsetMeters?: number;
  readonly vehicleSpeedMetersPerSecond?: number;
  readonly vehicleHeadingYawRadians?: number;
  readonly vehicleLookBehind?: boolean;
}

export interface CameraRigState {
  readonly target: RenderVector3;
  readonly desiredPosition: RenderVector3;
  readonly resolvedPosition: RenderVector3;
  readonly desiredDistanceMeters: number;
  readonly resolvedDistanceMeters: number;
  readonly obstructingObjectIds: readonly string[];
}

function copyVector(vector: Readonly<Vector3>): RenderVector3 {
  return { x: vector.x, y: vector.y, z: vector.z };
}

function responseAlpha(responsePerSecond: number, deltaSeconds: number): number {
  if (
    !Number.isFinite(responsePerSecond) ||
    responsePerSecond <= 0 ||
    !Number.isFinite(deltaSeconds) ||
    deltaSeconds <= 0
  ) {
    return 1;
  }
  return 1 - Math.exp(-responsePerSecond * deltaSeconds);
}

export class ThirdPersonCameraRig {
  readonly camera: PerspectiveCamera;

  private pitchRadians: number;
  private distanceMeters: number;
  private collisionQuery: CameraCollisionQuery | undefined;
  private readonly target = new Vector3();
  private readonly desiredPosition = new Vector3();
  private readonly lookTarget = new Vector3();
  private readonly segment = new Vector3();
  private readonly config: CameraRenderConfig;
  private groundedFarMeters: number;
  private flightFarMeters: number;
  private aerialVisibilityEnabled = false;
  private verticalTargetInitialized = false;
  private planarVehicleTargetInitialized = false;
  private state: CameraRigState;

  constructor(
    config: CameraRenderConfig,
    collisionQuery?: CameraCollisionQuery,
  ) {
    this.config = config;
    this.pitchRadians = config.initialPitchRadians;
    this.distanceMeters = config.distanceMeters;
    this.groundedFarMeters = config.farMeters;
    this.flightFarMeters = config.flightFarMeters;
    this.collisionQuery = collisionQuery;
    this.camera = new PerspectiveCamera(
      config.fovDegrees,
      1,
      config.nearMeters,
      config.farMeters,
    );
    const origin = { x: 0, y: 0, z: 0 };
    this.state = {
      target: origin,
      desiredPosition: origin,
      resolvedPosition: origin,
      desiredDistanceMeters: 0,
      resolvedDistanceMeters: 0,
      obstructingObjectIds: [],
    };
  }

  pitchBy(deltaPixelsY: number): void {
    this.pitchRadians = MathUtils.clamp(
      this.pitchRadians +
        deltaPixelsY * this.config.orbitRadiansPerPixel,
      this.config.minPitchRadians,
      this.config.maxPitchRadians,
    );
  }

  resetPitch(): void {
    this.pitchRadians = this.config.initialPitchRadians;
  }

  zoomBy(deltaPixelsY: number): void {
    this.distanceMeters = MathUtils.clamp(
      this.distanceMeters + deltaPixelsY * this.config.zoomMetersPerPixel,
      this.config.minDistanceMeters,
      this.config.maxDistanceMeters,
    );
  }

  setCollisionQuery(query: CameraCollisionQuery | undefined): void {
    this.collisionQuery = query;
  }

  setAspect(aspect: number): void {
    if (!Number.isFinite(aspect) || aspect <= 0) {
      return;
    }

    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  setAerialVisibilityEnabled(enabled: boolean): void {
    this.aerialVisibilityEnabled = enabled;
    this.applyVisibilityFarPlane();
  }

  setVisibilityFarMeters(
    groundedFarMeters: number,
    flightFarMeters: number,
  ): void {
    for (const [label, value] of [
      ["grounded", groundedFarMeters],
      ["flight", flightFarMeters],
    ] as const) {
      if (!Number.isFinite(value) || value <= this.camera.near) {
        throw new Error(
          `${label} camera far distance must exceed the near plane`,
        );
      }
    }
    this.groundedFarMeters = groundedFarMeters;
    this.flightFarMeters = flightFarMeters;
    this.applyVisibilityFarPlane();
  }

  private applyVisibilityFarPlane(): void {
    const farMeters = this.aerialVisibilityEnabled
      ? this.flightFarMeters
      : this.groundedFarMeters;
    if (this.camera.far === farMeters) {
      return;
    }
    this.camera.far = farMeters;
    this.camera.updateProjectionMatrix();
  }

  update(update: Readonly<CameraRigUpdate>): Readonly<CameraRigState> {
    const vehicleSpeed = Math.max(0, update.vehicleSpeedMetersPerSecond ?? 0);
    const vehicleFactor = MathUtils.clamp(
      vehicleSpeed / ROAD_VEHICLE_CAMERA_TUNING.fullEffectSpeedMps,
      0,
      1,
    );
    const vehicleMode = update.vehicleHeadingYawRadians !== undefined;
    const viewYaw = update.viewYawRadians +
      (vehicleMode && update.vehicleLookBehind === true ? Math.PI : 0);
    const activeDistance = (update.distanceMeters ?? this.distanceMeters) +
      vehicleFactor * ROAD_VEHICLE_CAMERA_TUNING.maximumPullbackM;
    const horizontalDistance =
      Math.cos(this.pitchRadians) * activeDistance;
    const verticalDistance =
      Math.sin(this.pitchRadians) * activeDistance;
    const forwardX = Math.sin(viewYaw);
    const forwardZ = Math.cos(viewYaw);
    const rightX = Math.cos(viewYaw);
    const rightZ = -Math.sin(viewYaw);
    const desiredTargetY =
      update.playerPosition.y +
      this.config.targetHeightMeters +
      (update.targetHeightOffsetMeters ?? 0);

    const vehicleHeading = update.vehicleHeadingYawRadians ?? viewYaw;
    const lookAhead = vehicleMode
      ? vehicleFactor * ROAD_VEHICLE_CAMERA_TUNING.maximumLookAheadM
      : 0;
    const desiredTargetX = update.playerPosition.x +
      Math.sin(vehicleHeading) * lookAhead;
    const desiredTargetZ = update.playerPosition.z +
      Math.cos(vehicleHeading) * lookAhead;
    if (vehicleMode) {
      if (!this.planarVehicleTargetInitialized) {
        this.target.x = desiredTargetX;
        this.target.z = desiredTargetZ;
        this.planarVehicleTargetInitialized = true;
      } else {
        const planarAlpha = responseAlpha(
          ROAD_VEHICLE_CAMERA_TUNING.planarFollowResponsePerSecond,
          update.deltaSeconds,
        );
        this.target.x = MathUtils.lerp(this.target.x, desiredTargetX, planarAlpha);
        this.target.z = MathUtils.lerp(this.target.z, desiredTargetZ, planarAlpha);
      }
    } else {
      this.planarVehicleTargetInitialized = false;
      this.target.x = desiredTargetX;
      this.target.z = desiredTargetZ;
    }
    if (!this.verticalTargetInitialized) {
      this.target.y = desiredTargetY;
      this.verticalTargetInitialized = true;
    } else {
      const verticalResponse =
        this.config.verticalFollowResponsePerSecond;
      this.target.y = verticalResponse === undefined
        ? desiredTargetY
        : MathUtils.lerp(
            this.target.y,
            desiredTargetY,
            responseAlpha(verticalResponse, update.deltaSeconds),
          );
    }

    this.desiredPosition.set(
      this.target.x -
        forwardX * horizontalDistance +
        rightX * (update.shoulderOffsetMeters ?? this.config.shoulderOffsetMeters),
      this.target.y + verticalDistance,
      this.target.z -
        forwardZ * horizontalDistance +
        rightZ * (update.shoulderOffsetMeters ?? this.config.shoulderOffsetMeters),
    );

    this.segment.subVectors(this.desiredPosition, this.target);
    const desiredDistanceMeters = this.segment.length();
    let resolvedDistanceMeters = desiredDistanceMeters;
    let obstructingObjectIds: readonly string[] = [];
    if (this.collisionQuery !== undefined && desiredDistanceMeters > 0) {
      const result = this.collisionQuery({
        from: copyVector(this.target),
        to: copyVector(this.desiredPosition),
        radiusMeters: this.config.collisionRadiusMeters ?? 0,
      });
      obstructingObjectIds = [...result.obstructingObjectIds];
      if (
        result.maximumDistanceMeters !== null &&
        Number.isFinite(result.maximumDistanceMeters)
      ) {
        resolvedDistanceMeters = MathUtils.clamp(
          result.maximumDistanceMeters -
            (this.config.collisionBufferMeters ?? 0),
          0,
          desiredDistanceMeters,
        );
      }
    }

    this.camera.position.copy(this.target);
    if (desiredDistanceMeters > 0) {
      this.camera.position.addScaledVector(
        this.segment,
        resolvedDistanceMeters / desiredDistanceMeters,
      );
    }

    this.lookTarget.set(
      this.target.x + rightX * this.config.shoulderOffsetMeters * 0.18,
      this.target.y,
      this.target.z + rightZ * this.config.shoulderOffsetMeters * 0.18,
    );
    this.camera.lookAt(this.lookTarget);

    const targetFov = vehicleMode
      ? this.config.fovDegrees +
        vehicleFactor * ROAD_VEHICLE_CAMERA_TUNING.maximumFovIncreaseDegrees
      : update.isSprinting
      ? (this.config.sprintFovDegrees ?? this.config.fovDegrees)
      : this.config.fovDegrees;
    const fovResponse = this.config.fovResponsePerSecond;
    const nextFov = fovResponse === undefined
      ? targetFov
      : MathUtils.lerp(
          this.camera.fov,
          targetFov,
          responseAlpha(fovResponse, update.deltaSeconds),
        );
    if (nextFov !== this.camera.fov) {
      this.camera.fov = nextFov;
      this.camera.updateProjectionMatrix();
    }
    this.camera.updateMatrixWorld();

    this.state = {
      target: copyVector(this.target),
      desiredPosition: copyVector(this.desiredPosition),
      resolvedPosition: copyVector(this.camera.position),
      desiredDistanceMeters,
      resolvedDistanceMeters,
      obstructingObjectIds,
    };
    return this.state;
  }

  getState(): Readonly<CameraRigState> {
    return this.state;
  }
}
